import React from 'react';
import cx from 'classnames';

import { Input as IInput, InputProps as IInputProps, Skeleton } from "@heroui/react";
import { Textarea , TextAreaProps as TextareaProps } from "@heroui/react";

import FlexRow from './FlexRow';

export interface InputProps extends IInputProps {
  isLoaded?:			boolean;
  onKeyDown?:			(evt: any) => any;
  onEnterPressed?:		() => any;
  background?:			string;
}

const Input = (iprops: InputProps) => {
  const {background, baseRef, variant, isLoaded, labelPlacement, label, classNames, onEnterPressed, onKeyDown, type, endContent, ...props} = iprops;
  const innerRef = React.useRef(null as HTMLDivElement | null);
  const kdRef = React.useRef((evt: any) => { return; });

  // Password fields get a built-in show/hide eye toggle. We swap the input
  // type between "password" and "text" and render the eye as endContent —
  // unless the caller already supplied their own endContent.
  const [showPassword, setShowPassword] = React.useState(false);
  const isPassword = type === 'password';
  const effectiveType = isPassword && showPassword ? 'text' : type;
  const passwordToggle = isPassword
    ?	<span className="material-icons cursor-pointer !text-[20px] !text-[--Dark-Grey] select-none"
              role="button"
              tabIndex={-1}
              onClick={() => setShowPassword((v) => !v)}>
          { showPassword ? 'visibility_off' : 'visibility' }
        </span>
    :	undefined;
  const resolvedEndContent = endContent ?? passwordToggle;
  
  const v = variant === undefined
  			?	"bordered"
  			:	variant;
  
  const lp =	labelPlacement === undefined
  				?	"inside"
  				:	labelPlacement;
  
  const loaded = isLoaded === undefined
  				 ?	true
  				 :	isLoaded;
  
  const bg =	background === undefined
  				?	''
  				:	background;
  
  kdRef.current = React.useCallback((evt: any) => {
    if (onKeyDown)
      onKeyDown(evt);
    
    //console.log("Input/handleKeyDown/evt=", evt);
    const code =	evt.code
    				?	evt.code
    				:	evt.key;
    
    switch(code) {
    case "Enter":
      //console.log("Input/handleKeyDown/Enter/onEnterPressed=", onEnterPressed);
      if (onEnterPressed)
        onEnterPressed();
      break;
    }
  }, [onEnterPressed, onKeyDown]);
  
  React.useEffect(() => {
    if (!innerRef.current)
      return;
    
    const inputElem = innerRef.current.querySelector("input");
    if (!inputElem)
      return;
    
    inputElem.addEventListener("keydown", (evt: any) => kdRef.current(evt));
  }, [innerRef.current]);
  
  // Caller's per-slot classNames. The previous implementation
  // discarded the entire object by passing it whole into cx() for the
  // inputWrapper slot — which produced "[object Object]" garbage and
  // dropped every other slot the caller wanted to style (input, label,
  // helperWrapper, etc.). Result: callers could not change input text
  // color or label positioning, and the default `bg-white` on the label
  // (combined with HeroUI v2.7's changed label-floating behaviour) sat
  // OPAQUELY over the typed text — the "I can't see what I'm typing"
  // bug on the login screen.
  //
  // Fix: merge caller's per-slot classes onto the defaults via cx(),
  // so callers can append/override any slot.
  const callerCn = (classNames ?? {}) as Record<string, string | undefined>;
  // Render strategy depends on label placement:
  //   - inside  → HeroUI's floating label (works; `input-notched` lifts it
  //               out of the box on focus/fill).
  //   - outside → render the label OURSELVES as a plain block above the
  //               input and give HeroUI no label. HeroUI v2.7's outside-
  //               label positioning rendered the label INSIDE the box,
  //               overlapping the value/placeholder — the "labels sitting on
  //               top of the text" bug on the Login + Mailer forms. This
  //               mirrors the working email-flow forms in pages/Account,
  //               which place their own label above a label-less HeroUI input.
  const isOutside = lp === 'outside';

  const heroInput =
    <IInput variant={v}
            baseRef={innerRef}
            {...(isOutside ? {} : { label, labelPlacement: lp })}
            classNames={{
              inputWrapper: cx(
                "px-[16px] py-[8px] rounded-[4px] border-solid border-1 border-[--Mid-Grey] max-h-[52px]",
                bg,
                callerCn.inputWrapper
              ),
              innerWrapper: cx('!items-center', callerCn.innerWrapper),
              // input-notched lifts a FLOATING (inside) label out of the box
              // on focus/fill; only meaningful for inside placement.
              label: cx(lp === 'inside' && 'input-notched', 'px-[4px]', callerCn.label),
              input: cx('!text-black', callerCn.input),
              mainWrapper: callerCn.mainWrapper,
              base: callerCn.base,
              description: callerCn.description,
              errorMessage: callerCn.errorMessage,
              helperWrapper: callerCn.helperWrapper,
              clearButton: callerCn.clearButton,
            }}
            {...props}
            type={effectiveType}
            endContent={resolvedEndContent} />;

  const out = isOutside && label
    ?	<FlexRow direction="column" align="start" className="w-full gap-[4px]">
          <span className={cx('text-[13px] mb-[2px]', callerCn.label)}>
            { label }
          </span>
          { heroInput }
        </FlexRow>
    :	heroInput;

  if (!loaded)
    return (
    <Skeleton isLoaded={loaded}
              className="w-full">
      { out }
    </Skeleton>
    );

  return out;
}

export interface TextAreaProps extends TextareaProps {
  isLoaded?:			boolean;
  showChars?:			boolean;
  maxChars?:			number;
}

export const TextArea = (tprops: TextAreaProps) => {
  const {variant, classNames, isLoaded, maxChars, showChars, ...props} = tprops;
  
  const sc =	showChars === undefined
  				?	true
  				:	showChars;
  
  const v = variant === undefined
  			?	"faded"
  			:	variant;
  
  const loaded = isLoaded === undefined
  				 ?	true
  				 :	isLoaded;
  
  const out = (
  <Textarea variant={v}
            classNames={{
              inputWrapper: cx(
                "px-[16px] py-[8px] rounded-[4px] border-solid border-1 border-[--Mid-Grey] min-h-[120px] bg-transparent",
                classNames ? classNames.inputWrapper : "",
              ),
              label: "!text-black r10",
              input: "!text-black r14",
            }}
            {...props} />
  );
  
  const ta =	!loaded
  				?	<Skeleton isLoaded={loaded}
  				              className="w-full">
					  { out }
					</Skeleton>
				:	out;
  
  const mc =	maxChars === undefined
    			?	200
    			:	maxChars;
  
  const len =	props.value
  				?	props.value.length
  				:	0;
  
  return (
  <FlexRow direction="column">
    { ta }
  {sc &&
    <FlexRow justify="end"
             className="text-[--Dark-Grey] text-[10px] pt-[3px]">
      { len + "/" + mc }
    </FlexRow>
  }
  </FlexRow>
  )
};

export default Input;
